import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useSignals, type SignalKind } from '../hooks/useSignals';

/**
 * A compact "this page needs attention" chip for a domain page header. Reuses
 * the workspace Signals feed (Wave 0) filtered to the kinds relevant to the
 * current page, and deep-links to the home command center where the full ranked
 * feed lives. Renders nothing when the page has no open signals, so it stays
 * unobtrusive and only appears when there is real, actionable work.
 */
export function PageSignalsLink({ kinds }: { kinds: SignalKind[] }) {
  const { wsId } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data } = useSignals(wsId);

  const count = (data ?? []).filter((s) => kinds.includes(s.kind)).length;
  if (count === 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(`/app/workspace/${wsId}/home`)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-accent-300 dark:border-accent-700 text-accent-700 dark:text-accent-200 text-xs font-medium hover:bg-accent-50 dark:hover:bg-accent-900/30"
      title={t('signals.pageHint', { count })}
    >
      <Sparkles size={13} />
      {t('signals.pageLabel', { count })}
      <ArrowRight size={13} />
    </button>
  );
}
